import { createContext, type ReactNode, use } from 'react'
import { LOCALE_QUERY_PARAM } from './locales'

/**
 * Which language a marketing page is rendered in.
 *
 * Deliberately *not* the app's Lingui locale. The marketing surface renders
 * without `LocaleProvider` (see `src/lib/marketing-paths.ts`), and its
 * language is a property of the URL — `/` is pt-BR, `/en/**` is English —
 * not of the visitor's stored preference. Keying off `localStorage` the way
 * the app does would tear the prerendered HTML: `/en` is prerendered once at
 * build time, so a pt-BR-defaulted browser would hydrate it into a different
 * language than the static markup it just painted.
 */
export type MarketingLocale = 'pt-BR' | 'en'

/*
 * Defaults to pt-BR so every existing route keeps working with no provider
 * around it — only the `/en/**` routes wrap their tree.
 */
const MarketingLocaleContext = createContext<MarketingLocale>('pt-BR')

export const MarketingLocaleProvider = ({
  locale,
  children,
}: {
  locale: MarketingLocale
  children: ReactNode
}) => <MarketingLocaleContext value={locale}>{children}</MarketingLocaleContext>

export const useMarketingLocale = (): MarketingLocale => use(MarketingLocaleContext)

/**
 * Picks this page's entry out of a locale-keyed map. The idiom for content
 * that differs only in wording — sample data inside the product mockups,
 * chrome labels — where duplicating the markup would be the wrong trade.
 */
export const useLocalized = <T,>(byLocale: Record<MarketingLocale, T>): T =>
  byLocale[useMarketingLocale()]

/**
 * Builds the URL for a link that leaves the marketing surface for an app
 * route (`/sign-up`, `/sign-in`), carrying the page's language across in
 * `?lang=` so the app doesn't fall back to its pt-BR default.
 *
 * Callers must render the result as a plain `<a href>`, never a router
 * `<Link>`: `getMarketingRouter()` converts in-router navigations that leave
 * the marketing surface into `window.location.assign(toLocation.href)`, and
 * that href is rebuilt from the destination route's `validateSearch`. Neither
 * `/sign-up` nor `/sign-in` declares `lang`, so the parameter would be
 * **dropped on the way out**. An anchor bypasses the router entirely.
 */
export const buildAppHref = ({
  locale,
  search,
  to,
}: {
  locale: MarketingLocale
  search?: Record<string, string>
  to: string
}): string => {
  const params = new URLSearchParams(search)
  params.set(LOCALE_QUERY_PARAM, locale)
  return `${to}?${params.toString()}`
}

/** `<html lang>` and `og:locale` values for each marketing locale. */
export const HTML_LANG: Record<MarketingLocale, string> = {
  en: 'en',
  'pt-BR': 'pt-BR',
}

export const OG_LOCALE: Record<MarketingLocale, string> = {
  en: 'en_US',
  'pt-BR': 'pt_BR',
}
